// src/components/CreateProduct/CreateProduct.jsx

import React, { useEffect, useState } from "react";
import { AiOutlinePlusCircle, AiOutlineDelete } from "react-icons/ai";
import { useDispatch, useSelector } from "react-redux";
import { createProduct, clearErrors } from "../../redux/actions/product";
import { categoriesData } from "../../static/data";
import { toast } from "react-toastify";
import imageCompression from "browser-image-compression";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";

const CreateProduct = () => {
  const { seller } = useSelector((state) => state.seller || {});
  const { success, error, isLoading } = useSelector(
    (state) => state.products || {}
  );

  const dispatch = useDispatch();

  const [images, setImages] = useState([]);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState("");
  const [tags, setTags] = useState("");
  const [stock, setStock] = useState("");
  const [gstPercentage, setGstPercentage] = useState("");
  const [hsnCode, setHsnCode] = useState("");
  const [leadTime, setLeadTime] = useState("Immediate");
  const [pricePerQty, setPricePerQty] = useState("Nos");
  const [customPricePerQty, setCustomPricePerQty] = useState("");
  const [stockAvailability, setStockAvailability] = useState("All time stock available");
  const [minimumOrderQuantity, setMinimumOrderQuantity] = useState(1);
  const [serviceableLocation, setServiceableLocation] = useState("more_than_1000kms");
  // After existing useState declarations
const [hasVariants, setHasVariants] = useState(false);
const [variantTypes, setVariantTypes] = useState([]);
const [variantItems, setVariantItems] = useState({});
const [variantStock, setVariantStock] = useState({});


  const [quantitySlabs, setQuantitySlabs] = useState([
    {
      min: 1,
      max: "",
      originalPrice: "",
      discountPrice: "",
      totalPrice: "",
    },
  ]);

  // Handle Image Selection with Compression and Size Validation
  const handleImageChange = async (e) => {
    const files = Array.from(e.target.files);
    const compressedImages = [];

    for (let file of files) {
      try {
        // Compress the image
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };
        const compressedFile = await imageCompression(file, options);

        // Validate compressed image size
        if (compressedFile.size / 1024 / 1024 > 1) {
          toast.error(`${file.name} is larger than 1MB after compression.`);
          continue;
        }

        // Create a preview URL
        const preview = URL.createObjectURL(compressedFile);

        compressedImages.push({ file: compressedFile, preview });
      } catch (error) {
        toast.error(`Error compressing ${file.name}: ${error.message}`);
      }
    }

    setImages((prev) => [...prev, ...compressedImages]);
  };

  // Handle Image Deletion
  const handleDeleteImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  // Handle Drag End for Rearranging Images
  const handleOnDragEnd = (result) => {
    if (!result.destination) return;

    const reorderedImages = Array.from(images);
    const [movedImage] = reorderedImages.splice(result.source.index, 1);
    reorderedImages.splice(result.destination.index, 0, movedImage);

    setImages(reorderedImages);
  };

  const handleAddSlab = () => {
    const lastSlab = quantitySlabs[quantitySlabs.length - 1];
    setQuantitySlabs([
      ...quantitySlabs,
      {
        min: lastSlab.max ? parseInt(lastSlab.max) + 1 : lastSlab.min + 1,
        max: "",
        originalPrice: "",
        discountPrice: "",
        totalPrice: "",
      },
    ]);
  };

  const handleRemoveSlab = (index) => {
    const newSlabs = quantitySlabs.filter((_, i) => i !== index);
    setQuantitySlabs(newSlabs);
  };

  const validateForm = () => {
    if (
      !name ||
      !description ||
      !category ||
      images.length === 0 ||
      quantitySlabs.some(
        (slab) => !slab.max || !slab.originalPrice || !slab.discountPrice
      ) ||
      (pricePerQty === "Others" && !customPricePerQty)
    ) {
      toast.error("Please fill in all required fields.");
      return false;
    }
  
    if (!hasVariants) {
      if (
        stockAvailability === "Limited stock available" &&
        (!stock || parseInt(stock, 10) <= 0)
      ) {
        toast.error("Please enter a valid stock quantity.");
        return false;
      }
    } else {
      // Validate variant stocks
      for (const [key, data] of Object.entries(variantStock)) {
        if (
          data.availability === "Limited stock available" &&
          (!data.stock || parseInt(data.stock, 10) <= 0)
        ) {
          toast.error(
            `Please enter a valid stock quantity for variant combination: ${key}`
          );
          return false;
        }
      }
    }
  
    if (parseFloat(gstPercentage) < 0 || parseFloat(gstPercentage) > 100) {
      toast.error("GST percentage must be between 0 and 100.");
      return false;
    }
    return true;
  };
  

  // Function to handle adding a new variant type
const handleAddVariantType = () => {
  setVariantTypes([...variantTypes, ""]);
};

// Function to handle removing a variant type
const handleRemoveVariantType = (index) => {
  const newVariantTypes = [...variantTypes];
  const removedType = newVariantTypes.splice(index, 1)[0];
  setVariantTypes(newVariantTypes);

  // Remove associated variant items
  const newVariantItems = { ...variantItems };
  delete newVariantItems[removedType];
  setVariantItems(newVariantItems);
};

// Function to handle changing a variant type
const handleVariantTypeChange = (index, value) => {
  const newVariantTypes = [...variantTypes];
  newVariantTypes[index] = value;
  setVariantTypes(newVariantTypes);
};

// Function to handle adding a variant item
const handleAddVariantItem = (variantType) => {
  const items = variantItems[variantType] || [];
  setVariantItems({
    ...variantItems,
    [variantType]: [...items, ""],
  });
};

// Function to handle removing a variant item
const handleRemoveVariantItem = (variantType, idx) => {
  const items = variantItems[variantType];
  const newItems = items.filter((_, i) => i !== idx);
  setVariantItems({
    ...variantItems,
    [variantType]: newItems,
  });
};

// Function to handle changing a variant item
const handleVariantItemChange = (variantType, idx, value) => {
  const items = variantItems[variantType];
  items[idx] = value;
  setVariantItems({
    ...variantItems,
    [variantType]: items,
  });
};

// Function to generate all combinations of variant items
const generateVariantCombinations = () => {
  const variantTypesArray = variantTypes;
  const variantItemsArray = variantTypesArray.map(
    (type) => variantItems[type] || []
  );
  if (variantItemsArray.some((items) => items.length === 0)) {
    return [];
  }
  const combinations = cartesianProduct(...variantItemsArray);
  return combinations.map((combination) =>
    combination.map((value, index) => ({
      variantType: variantTypesArray[index],
      value,
    }))
  );
};

// Helper function to compute cartesian product
function cartesianProduct(...arrays) {
  return arrays.reduce(
    (acc, curr) =>
      acc
        .map((x) => curr.map((y) => x.concat([y])))
        .reduce((a, b) => a.concat(b), []),
    [[]]
  );
}

// Function to handle variant stock change
const handleVariantStockChange = (combination, data) => {
  const key = getCombinationKey(combination);
  setVariantStock({
    ...variantStock,
    [key]: data,
  });
};


// Helper function to get a unique key for a combination
const getCombinationKey = (combination) => {
  return combination
    .map((item) => `${item.variantType}:${item.value}`)
    .join("|");
};

const handleSubmit = async (e) => {
  e.preventDefault();

  if (!validateForm()) {
    return;
  }
  const finalPricePerQty = pricePerQty === "Others" ? customPricePerQty : pricePerQty;
  const formData = new FormData();
  formData.append("name", name);
  formData.append("description", description);
  formData.append("category", category);
  formData.append("tags", tags);
  formData.append("serviceableLocation", serviceableLocation);
  formData.append("pricePerQty", finalPricePerQty);
  formData.append("stockAvailability", stockAvailability);

  console.log("Serviceable Location:", serviceableLocation);
  
  // Calculate total stock based on variants or direct input
  if (!hasVariants) {
    if (stockAvailability === "Limited stock available") {
      formData.append("stock", stock);
    } else {
      formData.append("stock", 0); // Stock is not required for other options
    }
    // Ensure variant fields are cleared
    formData.append("hasVariants", false);
    formData.append("variantTypes", JSON.stringify([]));
    formData.append("variantItems", JSON.stringify({}));
    formData.append("variantStock", JSON.stringify([]));
  } else {
    // For products with variants
    formData.append("hasVariants", true);
    formData.append("variantTypes", JSON.stringify(variantTypes));
    formData.append("variantItems", JSON.stringify(variantItems));

    // Prepare variantStockArray
    const variantStockArray = Object.entries(variantStock).map(
      ([combinationKey, data]) => ({
        combination: combinationKey,
        availability: data.availability,
        stock: data.stock || 0,
      })
    );
    formData.append("variantStock", JSON.stringify(variantStockArray));
  }

  formData.append("gstPercentage", gstPercentage);
  formData.append("hsnCode", hsnCode);
  formData.append("leadTime", leadTime);
  formData.append("shop", seller._id);
  formData.append("quantitySlabs", JSON.stringify(quantitySlabs));
  formData.append("minimumOrderQuantity", minimumOrderQuantity);
  // Append images as files
  images.forEach((img) => {
    formData.append("images", img.file);
  });
    try {
      await dispatch(createProduct(formData));
      // Clear form
      setImages([]);
      setName("");
      setDescription("");
      setCategory("");
      setTags("");
      setPricePerQty("Nos");
      setCustomPricePerQty("")
      setStock("");
      setGstPercentage("");
      setHsnCode("");
      setLeadTime("Immediate");
      setQuantitySlabs([
        {
          min: 1,
          max: "",
          originalPrice: "",
          discountPrice: "",
          totalPrice: "",
        },
      ]);
    } catch (error) {
      console.error("Error response:", error.response);
    }
  };

  useEffect(() => {
    setQuantitySlabs((prevSlabs) =>
      prevSlabs.map((slab) => {
        const discountPriceNumber = parseFloat(slab.discountPrice) || 0;
        const gstPercentageNumber = parseFloat(gstPercentage) || 0;
        const gstAmount = (discountPriceNumber * gstPercentageNumber) / 100;
        return {
          ...slab,
          totalPrice: (discountPriceNumber + gstAmount).toFixed(2),
        };
      })
    );
  }, [gstPercentage]);

  const handleSlabChange = (index, field, value) => {
    const newSlabs = [...quantitySlabs];
    newSlabs[index][field] = value;

    if (field === "discountPrice") {
      const discountPriceNumber =
        parseFloat(newSlabs[index].discountPrice) || 0;
      const gstPercentageNumber = parseFloat(gstPercentage) || 0;
      const gstAmount = (discountPriceNumber * gstPercentageNumber) / 100;
      newSlabs[index].totalPrice = (
        discountPriceNumber + gstAmount
      ).toFixed(2);
    }

    setQuantitySlabs(newSlabs);
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearErrors());
    }
    if (success) {
      toast.success("Product created successfully!");
      // Clear form fields if required
      setImages([]);
      setName("");
      setDescription("");
      setCategory("");
      setTags("");
      setPricePerQty("Nos");
      setStock("");
      setGstPercentage("");
      setHsnCode("");
      setLeadTime("Immediate");
      setQuantitySlabs([
        {
          min: 1,
          max: "",
          originalPrice: "",
          discountPrice: "",
          totalPrice: "",
        },
      ]);
    }
  }, [dispatch, error, success]);

  return (
    <div className="w-[90%] 800px:w-[50%] bg-white shadow h-[80vh] rounded-[4px] p-3 overflow-y-scroll">
      {isLoading ? (
        <div className="flex justify-center items-center h-full">
          <p className="text-lg font-semibold">Creating product ... Please dont navigate away...</p>
        </div>
      ) : (
        <>
          <h5 className="text-[30px] font-Poppins text-center">
            Create Product
          </h5>
          <form onSubmit={handleSubmit}>
            <br />
            {/* Name */}
            <div>
              <label className="pb-2">
                Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={name}
                className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                onChange={(e) => setName(e.target.value)}
                placeholder="Enter your product name..."
              />
            </div>
            <br />
            {/* Description */}
            <div>
              <label className="pb-2">
                Description <span className="text-red-500">*</span>
              </label>
              <textarea
                cols="30"
                required
                rows="8"
                type="text"
                name="description"
                value={description}
                className="mt-2 appearance-none block w-full pt-2 px-3 border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Enter your product description..."
              ></textarea>
            </div>
            <br />
            {/* Category */}
            <div>
              <label className="pb-2">
                Category <span className="text-red-500">*</span>
              </label>
              <select
                className="w-full mt-2 border h-[35px] rounded-[5px]"
                value={category}
                onChange={(e) => setCategory(e.target.value)}
              >
                <option value="">Choose a category</option>
                {categoriesData &&
                  categoriesData.map((i) => (
                    <option value={i.title} key={i.title}>
                      {i.title}
                    </option>
                  ))}
              </select>
            </div>
            <br />
            <br />
{/* Product Variants */}
<div>
  <label className="pb-2">
    Product Variants <span className="text-red-500">*</span>
  </label>
  <div className="mt-2">
    <label>
      <input
        type="radio"
        name="hasVariants"
        value="no"
        checked={!hasVariants}
        onChange={() => setHasVariants(false)}
      />{" "}
      No
    </label>
    <label className="ml-4">
      <input
        type="radio"
        name="hasVariants"
        value="yes"
        checked={hasVariants}
        onChange={() => setHasVariants(true)}
      />{" "}
      Yes
    </label>
  </div>
</div>

{hasVariants && (
  <>
    <br />
    {/* Variant Types */}
<div className="mt-6">
  <label className="pb-2 font-semibold text-lg">
    Variant Types <span className="text-red-500">*</span>
  </label>
  {variantTypes.map((variantType, index) => (
    <div key={index} className="flex items-center mt-2">
      <input
        type="text"
        value={variantType}
        onChange={(e) => handleVariantTypeChange(index, e.target.value)}
        className="w-full px-3 h-[35px] border border-gray-300 rounded-l-[3px]"
        placeholder="Enter variant type (e.g., Size, Color)"
      />
      <button
        type="button"
        className="ml-1 px-3 py-1 bg-red-500 text-white rounded-r-[3px]"
        onClick={() => handleRemoveVariantType(index)}
      >
        Remove
      </button>
    </div>
  ))}
  <button
    type="button"
    className="mt-2 px-4 py-2 bg-green-500 text-white rounded"
    onClick={handleAddVariantType}
  >
    Add Variant Type
  </button>
</div>


    {/* Variant Items */}
{variantTypes.map((variantType, index) => (
  <div key={index} className="mt-6">
    <label className="pb-2 font-semibold text-lg">
      {variantType} Items <span className="text-red-500">*</span>
    </label>
    {(variantItems[variantType] || []).map((item, idx) => (
      <div key={idx} className="flex items-center mt-2">
        <input
          type="text"
          value={item}
          onChange={(e) =>
            handleVariantItemChange(variantType, idx, e.target.value)
          }
          className="w-full px-3 h-[35px] border border-gray-300 rounded-l-[3px]"
          placeholder={`Enter ${variantType} item`}
        />
        <button
          type="button"
          className="ml-1 px-3 py-1 bg-red-500 text-white rounded-r-[3px]"
          onClick={() => handleRemoveVariantItem(variantType, idx)}
        >
          Remove
        </button>
      </div>
    ))}
    <button
      type="button"
      className="mt-2 px-4 py-2 bg-green-500 text-white rounded"
      onClick={() => handleAddVariantItem(variantType)}
    >
      Add {variantType} Item
    </button>
  </div>
))}
    <br />
    
    {/* Variant Stock */}
<div className="mt-6">
  <label className="pb-2 font-semibold text-lg">
    Variant Stock <span className="text-red-500">*</span>
  </label>
  <div className="mt-2">
    {generateVariantCombinations().map((combination, index) => {
      const combinationKey = getCombinationKey(combination);
      const variantStockEntry = variantStock[combinationKey] || { availability: "All time stock available" };

      return (
        <div
          key={index}
          className="border-b border-gray-200 pb-4 mb-4"
        >
          <div className="flex items-center">
            <span className="w-full">
              {combination
                .map((item) => `${item.variantType}: ${item.value}`)
                .join(", ")}
            </span>
          </div>
          <div className="mt-2">
            <label className="mr-4">
              <input
                type="radio"
                name={`variantStockAvailability-${combinationKey}`}
                value="All time stock available"
                checked={
                  variantStockEntry.availability ===
                  "All time stock available"
                }
                onChange={(e) =>
                  handleVariantStockChange(combination, {
                    ...variantStockEntry,
                    availability: e.target.value,
                  })
                }
              />{" "}
              All time stock available
            </label>
            <label className="mr-4">
              <input
                type="radio"
                name={`variantStockAvailability-${combinationKey}`}
                value="Limited stock available"
                checked={
                  variantStockEntry.availability ===
                  "Limited stock available"
                }
                onChange={(e) =>
                  handleVariantStockChange(combination, {
                    ...variantStockEntry,
                    availability: e.target.value,
                  })
                }
              />{" "}
              Limited stock available
            </label>
            <label>
              <input
                type="radio"
                name={`variantStockAvailability-${combinationKey}`}
                value="Out of stock"
                checked={
                  variantStockEntry.availability === "Out of stock"
                }
                onChange={(e) =>
                  handleVariantStockChange(combination, {
                    ...variantStockEntry,
                    availability: e.target.value,
                  })
                }
              />{" "}
              Out of stock
            </label>
          </div>

          {/* If Limited stock is selected, show the stock quantity input */}
          {variantStockEntry.availability === "Limited stock available" && (
            <div className="mt-2">
              <label>Stock Quantity</label>
              <input
                type="number"
                value={variantStockEntry.stock || ""}
                onChange={(e) =>
                  handleVariantStockChange(combination, {
                    ...variantStockEntry,
                    stock: parseInt(e.target.value, 10),
                  })
                }
                className="w-20 px-3 h-[35px] border border-gray-300 rounded"
                placeholder="Stock"
              />
            </div>
          )}
        </div>
      );
    })}
  </div>
</div>
  </>
)}
<div>
  <label className="pb-2">
    Serviceable Area <span className="text-red-500">*</span>
  </label>
  <select
    className="w-full mt-2 border h-[35px] rounded-[5px]"
    value={serviceableLocation}
    onChange={(e) => setServiceableLocation(e.target.value)}
    required
  >
    <option value="more_than_1000kms">More than 1000 Kms (Default)</option>
    <option value="within_10kms">Within 10 Kms</option>
    <option value="within_50kms">Within 50 Kms</option>
    <option value="within_100kms">Within 100 Kms</option>
    <option value="within_1000kms">Within 1000 Kms</option>
  </select>
</div>
            {/* Tags */}
            <div>
              <label className="pb-2">Make (or) Brand <span className="text-red-500">*</span></label>
              <input
                type="text"
                name="tags"
                value={tags}
                className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                onChange={(e) => setTags(e.target.value)}
                placeholder="Enter your product Make..."
              />
            </div>
            <br />
            {/* Price Per Quantity */}
            <div>
              <label className="pb-2">Price Per Quantity <span className="text-red-500">*</span></label>
              <select
                className="w-full mt-2 border h-[35px] rounded-[5px]"
                value={pricePerQty}
                onChange={(e) => setPricePerQty(e.target.value)}
              >
                <option value="Nos">Nos</option>
                <option value="Kilogram">Kilogram</option>
                <option value="Gram">Gram</option>
                <option value="Meter">Meter</option>
                <option value="Millimeter">Millimeter</option>
                <option value="Centimeter">Centimeter</option>
                <option value="Foot">Foot</option>
                <option value="Square foot">Square foot</option>
                <option value="Litre">Litre</option>
                <option value="Millilitre">Millilitre</option>
                <option value="Cubic meter">Cubic meter</option>
                <option value="Dozen">Dozen</option>
                <option value="Others">Others</option>
              </select>
              {pricePerQty === "Others" && (
  <div className="mt-2">
    <label className="pb-2">Others- Type the custom quantity type <span className="text-red-500">*</span></label>
    <input
      type="text"
      name="customPricePerQty"
      value={customPricePerQty}
      className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
      onChange={(e) => setCustomPricePerQty(e.target.value)}
      placeholder="Enter custom price per quantity..."
      required
    />
  </div>
)}

            </div>
            
            <br />
            {/* GST Percentage */}
            <div>
              <label className="pb-2">
                GST Percentage<span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="gst"
                value={gstPercentage}
                className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                onChange={(e) => setGstPercentage(e.target.value)}
                placeholder="Enter your product GST percentage..."
              />
            </div>

            {/* HSN Code */}
            <div>
              <label className="pb-2">
                HSN Code <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                name="hsnCode"
                value={hsnCode}
                className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                onChange={(e) => setHsnCode(e.target.value)}
                placeholder="Enter your product HSN code ..."
              />
            </div>

            {/* Shop Lead Time */}
            <div>
              <label className="pb-2">
                Shop Lead time <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="leadTime"
                value={leadTime === 0 ? "Immediate" : leadTime}
                className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded-[3px] placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                onChange={(e) => {
                  if (e.target.value === "Immediate") {
                    setLeadTime(0);
                  } else {
                    setLeadTime(e.target.value);
                  }
                }}
                placeholder="Enter your product Lead Time ..."
              />
            </div>
            <div>
  <label className="pb-2">
    Minimum Order Quantity <span className="text-red-500">*</span>
  </label>
  <input
    type="number"
    name="minimumOrderQuantity"
    value={minimumOrderQuantity}
    onChange={(e) => setMinimumOrderQuantity(parseInt(e.target.value, 10) || 1)}
    className="mt-2 appearance-none block w-full px-3 h-[35px] border border-gray-300 rounded placeholder-gray-400 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
    min="1"
    required
    placeholder="Enter minimum order quantity (default is 1)"
  />
</div>
            <br />
            {/* Quantity Slabs */}
            <div>
              <label className="pb-2">
                Quantity Slabs <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-5 gap-2 mt-2 text-center font-semibold">
                <div>Min</div>
                <div>Max</div>
                <div>Original Price</div>
                <div>Discount Price</div>
                <div>Total Price with GST</div>
              </div>
              {quantitySlabs.map((slab, index) => (
                <div key={index} className="flex items-center mt-2">
                  <input
                    type="number"
                    value={slab.min}
                    readOnly
                    className="w-1/5 px-3 h-[35px] border border-gray-300 rounded-[3px] mr-2"
                    placeholder="Min"
                  />
                  <input
                    type="number"
                    value={slab.max}
                    className="w-1/5 px-3 h-[35px] border border-gray-300 rounded-[3px] mr-2"
                    placeholder="Max"
                    onChange={(e) =>
                      handleSlabChange(index, "max", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    value={slab.originalPrice}
                    className="w-1/5 px-3 h-[35px] border border-gray-300 rounded-[3px] mr-2"
                    placeholder="Original Price"
                    onChange={(e) =>
                      handleSlabChange(index, "originalPrice", e.target.value)
                    }
                  />
                  <input
                    type="number"
                    value={slab.discountPrice}
                    className="w-1/5 px-3 h-[35px] border border-gray-300 rounded-[3px] mr-2"
                    placeholder="Discount Price"
                    onChange={(e) =>
                      handleSlabChange(index, "discountPrice", e.target.value)
                    }
                  />
                  <input
                    type="text"
                    value={slab.totalPrice}
                    readOnly
                    className="w-1/5 px-3 h-[35px] border border-gray-300 rounded-[3px] mr-2"
                    placeholder="Total Price"
                  />
                  {index !== 0 && (
                    <button
                      type="button"
                      className="text-red-500"
                      onClick={() => handleRemoveSlab(index)}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              <button
                type="button"
                className="mt-2 bg-blue-500 text-white px-3 py-1 rounded"
                onClick={handleAddSlab}
              >
                Add Slab
              </button>
            </div>
            <br />
            {!hasVariants && (
  <>
  {/* Stock Availability */}
<div>
  <label className="pb-2">
    Stock Availability <span className="text-red-500">*</span>
  </label>
  <div className="mt-2">
    <label className="mr-4">
      <input
        type="radio"
        name="stockAvailability"
        value="All time stock available"
        checked={stockAvailability === "All time stock available"}
        onChange={(e) => setStockAvailability(e.target.value)}
      />{" "}
      All time stock available
    </label>
    <label className="mr-4">
      <input
        type="radio"
        name="stockAvailability"
        value="Limited stock available"
        checked={stockAvailability === "Limited stock available"}
        onChange={(e) => setStockAvailability(e.target.value)}
      />{" "}
      Limited stock available
    </label>
    <label>
      <input
        type="radio"
        name="stockAvailability"
        value="Out of stock"
        checked={stockAvailability === "Out of stock"}
        onChange={(e) => setStockAvailability(e.target.value)}
      />{" "}
      Out of stock
    </label>
  </div>
</div>

{/* If Limited stock is selected, show the stock quantity input */}
{stockAvailability === "Limited stock available" && (
  <div>
    <label className="pb-2">
      Stock Quantity <span className="text-red-500">*</span>
    </label>
    <input
      type="number"
      name="stock"
      value={stock}
      className="mt-2 block w-full px-3 h-[35px] border border-gray-300 rounded-[3px]"
      onChange={(e) => setStock(e.target.value)}
      placeholder="Enter your product stock..."
    />
  </div>
)}

    <br />
  </>
)}

            {/* Upload Images with Drag and Drop */}
            <div>
              <label className="pb-2">
                Upload Images <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center">
                <input
                  type="file"
                  name="images"
                  className="hidden"
                  accept=".jpg,.jpeg,.png"
                  multiple
                  onChange={handleImageChange}
                />
                <button
                  type="button"
                  className="cursor-pointer mt-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 focus:outline-none"
                  onClick={() =>
                    document.querySelector('input[name="images"]').click()
                  }
                >
                  <AiOutlinePlusCircle className="text-2xl text-gray-700" />
                </button>
              </div>
              {/* Drag and Drop Area */}
              <DragDropContext onDragEnd={handleOnDragEnd}>
                <Droppable droppableId="images" direction="horizontal">
                  {(provided) => (
                    <div
                      className="flex flex-wrap mt-2"
                      {...provided.droppableProps}
                      ref={provided.innerRef}
                    >
                      {images.map((image, index) => (
                        <Draggable
                          key={index}
                          draggableId={`image-${index}`}
                          index={index}
                        >
                          {(provided) => (
                            <div
                              className="relative mr-2 mb-2"
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            >
                              <img
                                src={image.preview}
                                alt="Product"
                                className="w-20 h-20 object-cover rounded"
                              />
                              <AiOutlineDelete
                                size={20}
                                className="absolute top-0 right-0 bg-red-500 rounded-full cursor-pointer"
                                onClick={() => handleDeleteImage(index)}
                              />
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            </div>
            <br />
            {/* Submit Button */}
            <div>
              <input
                type="submit"
                value="Create"
                className="cursor-pointer mt-2 block w-full h-[35px] border border-gray-300 rounded-[3px] bg-blue-500 text-white font-medium text-sm hover:bg-blue-600 focus:outline-none"
              />
            </div>
          </form>
        </>
      )}
    </div>
  );
};

export default CreateProduct;
